import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { fmtMoney, type Currency } from '../lib/money'
import { useCurrency } from '../lib/currency-context'
import { useAuth, authHeaders } from '../lib/auth'
import { useTranslation } from 'react-i18next'

// -----------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------
interface StripeSummary {
  sub_cents:     number
  deposit_cents: number
  fee_cents:     number
  net_cents:     number
  active_subs:   number
  total_cents:   number
}

interface StripePlan {
  price_id:           string
  price_amount_cents: number
  price_interval:     string
  active_count:       number
  total_sub_count:    number
  total_paid_cents:   number
  invoice_count:      number
}

interface StripeMonthly {
  month:         string
  sub_cents:     number
  deposit_cents: number
  fee_cents:     number
  net_cents:     number
}

interface StripeDeposit {
  id:            string
  customer_name: string | null
  amount_cents:  number
  created:       string
  status:        string
}

interface StripeOverview {
  year:            number
  period:          { from: string; to: string }
  summary:         StripeSummary
  plans:           StripePlan[]
  monthly:         StripeMonthly[]
  recent_deposits: StripeDeposit[]
}

// -----------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------
function pct(part: number, total: number): string {
  if (!total) return '—'
  return (part / total * 100).toFixed(1) + '%'
}

function planLabel(p: StripePlan): string {
  const interval = p.price_interval === 'year' ? '/yr' : `/${p.price_interval}`
  return fmtMoney(p.price_amount_cents, 'USD') + interval
}

function fmtMonth(yyyyMm: string): string {
  const [y, m] = yyyyMm.split('-')
  const d = new Date(Number(y), Number(m) - 1, 1)
  return d.toLocaleString('default', { month: 'short', year: 'numeric' })
}

// -----------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------
function SummaryCard({ label, cents, sub, currency, note }: {
  label:    string
  cents:    number
  sub?:     string
  currency: Currency
  note?:    string
}) {
  return (
    <Card className="p-5">
      <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">{label}</div>
      <div className="mt-2 text-[26px] font-bold text-ink-900 leading-none">
        {fmtMoney(cents, currency)}
      </div>
      {sub  && <div className="text-[11px] text-ink-500 mt-1">{sub}</div>}
      {note && <div className="text-[11px] text-mint-700 font-medium mt-1">{note}</div>}
    </Card>
  )
}

// -----------------------------------------------------------------------
// Main view
// -----------------------------------------------------------------------
export function StripeView() {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const { token }    = useAuth()
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear)

  const { data, isLoading } = useQuery<StripeOverview>({
    queryKey:    ['stripe-overview', year],
    queryFn:     () => fetch(`/api/stripe-overview?year=${year}`, { headers: authHeaders(token) }).then(r => r.json()),
    staleTime:   5 * 60_000,
  })

  const s = data?.summary
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2].filter(y => y >= 2024)

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Stripe Revenue</h1>
          <p className="text-sm text-ink-500 mt-0.5">
            Subscriptions · deposits · processing fees
          </p>
        </div>
        <select
          value={year}
          onChange={e => setYear(Number(e.target.value))}
          className="px-3 py-1.5 border border-ink-200 rounded-lg text-sm bg-white text-ink-700 font-medium"
        >
          {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {isLoading && (
        <div className="py-20 text-center text-ink-400 text-sm">Loading Stripe data…</div>
      )}

      {data && s && (<>

        {/* Summary KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard
            label="Total collected"
            cents={s.total_cents}
            sub={`${s.active_subs} active subscriber${s.active_subs !== 1 ? 's' : ''}`}
            currency={currency}
          />
          <SummaryCard
            label="Subscription revenue"
            cents={s.sub_cents}
            sub={pct(s.sub_cents, s.total_cents) + ' of volume'}
            currency={currency}
          />
          <SummaryCard
            label="Deposit payments"
            cents={s.deposit_cents}
            sub={pct(s.deposit_cents, s.total_cents) + ' of volume'}
            currency={currency}
          />
          <Card className="p-5 border-red-100">
            <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
              Stripe fees
            </div>
            <div className="mt-2 text-[26px] font-bold text-red-700 leading-none">
              {fmtMoney(s.fee_cents, currency)}
            </div>
            <div className="text-[11px] text-ink-500 mt-1">
              Net after fees: {fmtMoney(s.net_cents, currency)}
            </div>
            {s.total_cents > 0 && (
              <div className="text-[11px] text-flame-700 font-medium mt-1">
                {pct(s.fee_cents, s.total_cents)} effective rate
              </div>
            )}
          </Card>
        </div>

        {/* Plans breakdown */}
        <Card className="p-5">
          <div className="text-[14px] font-semibold text-ink-800 mb-4">
            Subscription plans
            <span className="ml-2 text-[11px] font-normal text-ink-400">active subscribers + {year} revenue per plan</span>
          </div>

          {data.plans.length === 0 ? (
            <div className="py-6 text-center text-ink-400 text-sm">No subscription data for {year}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-ink-500 border-b border-ink-200">
                    <th className="text-left py-2 pr-4">Plan</th>
                    <th className="text-right py-2 pr-4">Active subs</th>
                    <th className="text-right py-2 pr-4">All-time subs</th>
                    <th className="text-right py-2 pr-4">Invoices {year}</th>
                    <th className="text-right py-2 pr-4">Revenue {year}</th>
                    <th className="text-right py-2">Mix</th>
                  </tr>
                </thead>
                <tbody>
                  {data.plans.map(p => (
                    <tr key={p.price_id} className="border-b border-ink-100 hover:bg-ink-50">
                      <td className="py-2.5 pr-4">
                        <span className="font-semibold text-ink-900">{planLabel(p)}</span>
                        <span className="ml-2 text-[10px] text-ink-400 font-mono">{p.price_id.slice(-8)}</span>
                      </td>
                      <td className="py-2.5 pr-4 text-right">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          p.active_count > 0 ? 'bg-mint-50 text-mint-700' : 'bg-ink-100 text-ink-500'
                        }`}>
                          {p.active_count}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4 text-right text-ink-500 num">{p.total_sub_count}</td>
                      <td className="py-2.5 pr-4 text-right text-ink-500 num">{p.invoice_count}</td>
                      <td className="py-2.5 pr-4 text-right font-semibold num">
                        {fmtMoney(p.total_paid_cents, currency)}
                      </td>
                      <td className="py-2.5 text-right text-ink-500 text-[12px]">
                        {pct(p.total_paid_cents, s.sub_cents)}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-ink-200 font-semibold text-[13px]">
                    <td className="py-2 pr-4 text-ink-700">Total subscriptions</td>
                    <td className="py-2 pr-4 text-right text-ink-700">
                      {data.plans.reduce((a, p) => a + p.active_count, 0)}
                    </td>
                    <td className="py-2 pr-4 text-right text-ink-500">
                      {data.plans.reduce((a, p) => a + p.total_sub_count, 0)}
                    </td>
                    <td className="py-2 pr-4 text-right text-ink-500">
                      {data.plans.reduce((a, p) => a + p.invoice_count, 0)}
                    </td>
                    <td className="py-2 pr-4 text-right text-ink-900">
                      {fmtMoney(s.sub_cents, currency)}
                    </td>
                    <td className="py-2 text-right text-ink-500">100%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Monthly breakdown */}
        <Card className="p-5">
          <div className="text-[14px] font-semibold text-ink-800 mb-4">
            Monthly breakdown
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-ink-500 border-b border-ink-200">
                  <th className="text-left py-2 pr-4">Month</th>
                  <th className="text-right py-2 pr-4">Subscriptions</th>
                  <th className="text-right py-2 pr-4">Deposits</th>
                  <th className="text-right py-2 pr-4">Gross</th>
                  <th className="text-right py-2 pr-4 text-red-500">Fees</th>
                  <th className="text-right py-2">Net</th>
                </tr>
              </thead>
              <tbody>
                {[...data.monthly].reverse().map(r => {
                  const gross = r.sub_cents + r.deposit_cents
                  return (
                    <tr key={r.month} className="border-b border-ink-100 hover:bg-ink-50">
                      <td className="py-2 pr-4 font-medium text-ink-700">{fmtMonth(r.month)}</td>
                      <td className="py-2 pr-4 text-right num text-ink-600">{fmtMoney(r.sub_cents, currency)}</td>
                      <td className="py-2 pr-4 text-right num text-ink-600">{fmtMoney(r.deposit_cents, currency)}</td>
                      <td className="py-2 pr-4 text-right num font-semibold">{fmtMoney(gross, currency)}</td>
                      <td className="py-2 pr-4 text-right num text-red-600">−{fmtMoney(r.fee_cents, currency)}</td>
                      <td className="py-2 text-right num font-semibold text-mint-700">{fmtMoney(r.net_cents, currency)}</td>
                    </tr>
                  )
                })}
                {data.monthly.length === 0 && (
                  <tr><td colSpan={6} className="py-6 text-center text-ink-400">No data for {year}</td></tr>
                )}
              </tbody>
              {data.monthly.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-ink-200 font-semibold text-[13px]">
                    <td className="py-2 pr-4 text-ink-700">Total {year}</td>
                    <td className="py-2 pr-4 text-right num">{fmtMoney(s.sub_cents, currency)}</td>
                    <td className="py-2 pr-4 text-right num">{fmtMoney(s.deposit_cents, currency)}</td>
                    <td className="py-2 pr-4 text-right num">{fmtMoney(s.total_cents, currency)}</td>
                    <td className="py-2 pr-4 text-right num text-red-600">−{fmtMoney(s.fee_cents, currency)}</td>
                    <td className="py-2 text-right num text-mint-700">{fmtMoney(s.net_cents, currency)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>

        {/* Recent deposits */}
        {data.recent_deposits.length > 0 && (
          <Card className="p-5">
            <div className="text-[14px] font-semibold text-ink-800 mb-4">
              One-time deposit payments
              <span className="ml-2 text-[11px] font-normal text-ink-400">last 50 in {year}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-ink-500 border-b border-ink-200">
                    <th className="text-left py-2 pr-4">Date</th>
                    <th className="text-left py-2 pr-4">Customer</th>
                    <th className="text-right py-2 pr-4">Amount</th>
                    <th className="text-right py-2">Stripe ID</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_deposits.map(d => (
                    <tr key={d.id} className="border-b border-ink-100 hover:bg-ink-50">
                      <td className="py-2 pr-4 text-ink-500">{d.created}</td>
                      <td className="py-2 pr-4 font-medium">{d.customer_name ?? <span className="text-ink-400 italic">—</span>}</td>
                      <td className="py-2 pr-4 text-right num font-semibold">{fmtMoney(d.amount_cents, currency)}</td>
                      <td className="py-2 text-right font-mono text-[10px] text-ink-400">{d.id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

      </>)}
    </div>
  )
}
